import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  getTemplateParamGroups,
  updateTemplateParamGroup,
  type TemplateParamGroupQuery,
  changeTemplateParamGroupStatus,
} from '../api/templateParamGroupApi'
import { TEMPLATE_PARAMETER_KEY } from './useTemplateParameters'
import type { RecordStatusBody,  TemplateParamGroupUpsert } from '../types'

export const TEMPLATE_PARAM_GROUP_KEY = 'admin-template-param-groups'

/**
 * Parametre Grubu sunucu-durumu (059 US5).
 *
 * **`enabled` ile tembel yükleme (research D12):** grup **listesi** yalnız Gruplar sekmesi açıkken
 * çekilir; sekmeye girilene kadar hiçbir istek atılmaz. Grup **combosu** bundan ayrıdır ve
 * `useTemplateParameters` içinde her zaman yüklenir (Parametreler sekmesinin filtresidir).
 *
 * **Yazma sonrası iki anahtar birden tazelenir:** güncellenen grup hem grup listesinde hem de
 * Parametreler sekmesinin grup combosunda görünmelidir (spec US5 AS-2).
 *
 * **Ekleme/silme mutasyonu YOKTUR** (2026-08-03 kararı): ekranda gruplar yalnız düzenlenir ve durumu
 * değiştirilir. Sunucudaki uçlar yerinde durur ama istemci onları çağırmaz.
 */
export function useTemplateParamGroups(query: TemplateParamGroupQuery, enabled: boolean) {
  const qc = useQueryClient()
  const { i18n } = useTranslation()

  const list = useQuery({
    // `sort` anahtarın parçasıdır: kolon sıralaması sunucu-taraflıdır → değişince yeniden çekilmeli.
    queryKey: [TEMPLATE_PARAM_GROUP_KEY, i18n.language, query.page, query.size, query.sort ?? null],
    queryFn: () => getTemplateParamGroups(query),
    placeholderData: keepPreviousData,
    enabled,
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [TEMPLATE_PARAM_GROUP_KEY] })
    // Grup combosu parametre anahtarı altındadır → o da tazelenmeli.
    qc.invalidateQueries({ queryKey: [TEMPLATE_PARAMETER_KEY] })
  }

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: TemplateParamGroupUpsert }) =>
      updateTemplateParamGroup(id, body),
    onSuccess: invalidate,
  })

  const changeStatus = useMutation({
    mutationFn: ({ id, body }: { id: number; body: RecordStatusBody }) => changeTemplateParamGroupStatus(id, body),
    onSuccess: invalidate,
  })

  return { list, update, changeStatus }
}
